import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { ReadonlyObject } from 'schema/schema';
import { httpChecked, queryClient } from 'util/fetch';

const schema = Type.Union([
  ReadonlyObject({ logged_in: Type.Literal(false) }),
  ReadonlyObject({
    logged_in: Type.Literal(true),
    session_expires_in_minutes: Type.Number(),
  }),
]);

const checker = TypeCompiler.Compile(schema);

const queries = {
  check: queryOptions({
    queryKey: ['logged_in_status'] as const,
    queryFn: ({ signal }) =>
      httpChecked.get('/api/v2/logged_in_status', checker, {
        signal,
        skipLoadingIndicator: true,
      }),
  }),
};

export function useWillBeLoggedOut() {
  const { data } = useQuery({
    ...queries.check,
    staleTime: 10_000,
    refetchInterval: 60_000,
    select: (loggedInStatus) => {
      if (!loggedInStatus.logged_in) {
        return false;
      }

      const sessionExpiresInMinutes = loggedInStatus.session_expires_in_minutes;

      return sessionExpiresInMinutes <= 10;
    },
  });

  return data;
}

export function useIsLoggedIn() {
  const { data } = useQuery({
    ...queries.check,
    staleTime: 10_000,
    select: (loggedInStatus) => loggedInStatus.logged_in,
  });

  return data;
}

export async function prefetchLoggedInStatus() {
  await queryClient.prefetchQuery({ ...queries.check, staleTime: Infinity });
}

export function usePostponeLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['postpone_logout'],
    mutationFn: (postponedExpiryTime: boolean) =>
      httpChecked.post('/api/v2/logged_in_status', checker, {
        body: {
          logged_in_status: { postponed_expiry_time: postponedExpiryTime },
        },
      }),
    // ideally, we would use onSuccess and populate the cache, but the data
    // returned by the server is stale (`session_expires_in_minutes` is not
    // updated). It's still here as the backend might get fixed at some point
    // (and the data is more up to date, technically), but we still invalidate.
    onSuccess: (data) => {
      queryClient.setQueryData(queries.check.queryKey, data);
    },
    onSettled: () => queryClient.invalidateQueries(queries.check),
  });
}
